import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import { Database, Code2, Sparkles, Eye, EyeOff, Mail, Smartphone, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/v2`;

const AuthPage = ({ onLogin }) => {
  const [loginForm, setLoginForm] = useState({ identifier: "", password: "" }); // 'identifier' can be email or phone
  const [registerForm, setRegisterForm] = useState({ email: "", phone_number: "", password: "", full_name: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0); // 0-4 for strength
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [registerMethod, setRegisterMethod] = useState("email"); // "email" or "phone"
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerificationLoading, setOtpVerificationLoading] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [isRegistering, setIsRegistering] = useState(false); // To differentiate initial registration from OTP flow
  const [featureFlags, setFeatureFlags] = useState({
    FEATURE_EMAIL_REGISTRATION: false,
    FEATURE_PHONE_REGISTRATION: false,
  });

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        const response = await axios.get(`${API}/feature-flags/`);
        setFeatureFlags(response.data);
        // Set default registration method based on available flags
        if (response.data.FEATURE_EMAIL_REGISTRATION) {
          setRegisterMethod("email");
        } else if (response.data.FEATURE_PHONE_REGISTRATION) {
          setRegisterMethod("phone");
        }
      } catch (error) {
        console.error("Failed to fetch feature flags:", error);
        // Default to email if fetching fails
        setFeatureFlags({
          FEATURE_EMAIL_REGISTRATION: true,
          FEATURE_PHONE_REGISTRATION: false,
        });
        setRegisterMethod("email");
      }
    };
    fetchFeatureFlags();
  }, []);

  // Function to check password strength
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length > 7) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/\d/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    return strength;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = loginForm.identifier.includes('@')
        ? { email: loginForm.identifier, password: loginForm.password }
        : { phone_number: loginForm.identifier, password: loginForm.password };

      const response = await axios.post(`${API}/auth/login/`, payload);
      const token = response.data.access_token;

      // Get user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // toast.success("Welcome back!");
      onLogin(token, userResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFillDemo = () => {
    setLoginForm({
      identifier: "data.pilot@demo.demo",
      password: "Demo@123!"
    });
    toast.info("Demo credentials filled!");
  };


  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsRegistering(true); // Indicate that initial registration is in progress

    try {
      const payload = registerMethod === "email"
        ? { ...registerForm, phone_number: undefined }
        : { ...registerForm, email: undefined };

      await axios.post(`${API}/auth/register/`, payload);

      if (registerMethod === "email") {
        toast.success("Account created successfully! Please check your email to verify your account.");
      } else {
        toast.success("Account created successfully! An OTP has been sent to your phone number.");
        setOtpSent(true);
        setOtpResendTimer(60); // Start 60-second timer for OTP resend
        const timer = setInterval(() => {
          setOtpResendTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp/`, { phone_number: registerForm.phone_number });
      toast.success("New OTP sent successfully!");
      setOtpSent(true);
      setOtpResendTimer(60); // Reset timer
      const timer = setInterval(() => {
        setOtpResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpVerificationLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp/`, { phone_number: registerForm.phone_number, otp: otp });
      toast.success("Phone number verified successfully! You can now log in.");
      setOtpSent(false); // Reset OTP state
      setIsRegistering(false); // Reset registration state
      setOtp(""); // Clear OTP input
      // Optionally, switch to login tab or navigate
    } catch (error) {
      toast.error(error.response?.data?.detail || "OTP verification failed.");
    } finally {
      setOtpVerificationLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left side - Branding */}
        <div className="space-y-8 text-left">
          <div className="space-y-4">
            <div className="flex items-center gap-3" data-testid="logo-section">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-200">
                <Database className="h-7 w-7 text-white" />
              </div>
              <h1 className="text-5xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                DataPilot v2
              </h1>
            </div>
            <p className="text-lg text-slate-600 max-w-md">
              Your personal SQL learning environment. Practice queries safely with isolated databases.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200">
              <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Database className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Isolated Database</h3>
                <p className="text-sm text-slate-600">Your own SQLite database with sample data to practice</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200">
              <div className="h-10 w-10 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                <Code2 className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Smart SQL Editor</h3>
                <p className="text-sm text-slate-600">Syntax highlighting and query history tracking</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl border border-slate-200">
              <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Safe Learning</h3>
                <p className="text-sm text-slate-600">Query timeouts and easy database reset</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Auth form */}
        <Card className="border-slate-200 shadow-xl" data-testid="auth-card">
          <CardHeader>
            <CardTitle className="text-2xl">Get Started</CardTitle>
            <CardDescription>Login or create an account to start learning SQL</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login" data-testid="login-form">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-identifier">Email or Phone</Label>
                    <Input
                      id="login-identifier"
                      data-testid="login-identifier-input"
                      type="text"
                      placeholder="your.email@example.com or +1234567890"
                      value={loginForm.identifier}
                      onChange={(e) => setLoginForm({ ...loginForm, identifier: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                    {loading ? "Logging in..." : "Login"}
                  </Button>

                  <Alert className="bg-blue-50 border-blue-200 mt-6">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertTitle className="text-blue-800 font-semibold">Demo Credentials</AlertTitle>
                    <AlertDescription className="text-blue-700">
                      <div className="flex flex-col space-y-1 mt-2">
                        <div className="flex justify-between items-center">
                          <span>Email: <code className="bg-blue-100 px-1 rounded text-blue-900 font-mono text-xs">data.pilot@demo.demo</code></span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span>Pass: <code className="bg-blue-100 px-1 rounded text-blue-900 font-mono text-xs">Demo@123!</code></span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleFillDemo}
                          className="mt-2 border-blue-300 text-blue-700 hover:bg-blue-100 hover:text-blue-800 h-8 text-xs bg-white/50"
                        >
                          Auto-fill Demo Account
                        </Button>
                      </div>
                    </AlertDescription>
                  </Alert>
                </form>
              </TabsContent>

              <TabsContent value="register" data-testid="register-form">
                {!otpSent ? (
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Full Name</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        type="text"
                        placeholder="John Doe"
                        value={registerForm.full_name}
                        onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                        required
                      />
                    </div>

                    {!featureFlags.FEATURE_EMAIL_REGISTRATION && featureFlags.FEATURE_PHONE_REGISTRATION && (
                      <Button
                        type="button"
                        variant="default"
                        className="w-full"
                        disabled
                      >
                        <Smartphone className="h-4 w-4 mr-2" /> Register with Phone
                      </Button>
                    )}

                    {featureFlags.FEATURE_EMAIL_REGISTRATION && featureFlags.FEATURE_PHONE_REGISTRATION && (
                      <div className="flex items-center space-x-2">
                        <Button
                          type="button"
                          variant={registerMethod === "email" ? "default" : "outline"}
                          onClick={() => setRegisterMethod("email")}
                          className="flex-1"
                        >
                          <Mail className="h-4 w-4 mr-2" /> Register with Email
                        </Button>
                        <Button
                          type="button"
                          variant={registerMethod === "phone" ? "default" : "outline"}
                          onClick={() => setRegisterMethod("phone")}
                          className="flex-1"
                        >
                          <Smartphone className="h-4 w-4 mr-2" /> Register with Phone
                        </Button>
                      </div>
                    )}

                    {!featureFlags.FEATURE_EMAIL_REGISTRATION && !featureFlags.FEATURE_PHONE_REGISTRATION && (
                      <p className="text-center text-red-500">Registration is currently disabled.</p>
                    )}

                    {registerMethod === "email" && featureFlags.FEATURE_EMAIL_REGISTRATION && (
                      <div className="space-y-2">
                        <Label htmlFor="register-email">Email</Label>
                        <Input
                          id="register-email"
                          data-testid="register-email-input"
                          type="email"
                          placeholder="your.email@example.com"
                          value={registerForm.email}
                          onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                          required={registerMethod === "email"}
                        />
                      </div>
                    )}

                    {registerMethod === "phone" && featureFlags.FEATURE_PHONE_REGISTRATION && (
                      <div className="space-y-2">
                        <Label htmlFor="register-phone">Phone Number</Label>
                        <Input
                          id="register-phone"
                          data-testid="register-phone-input"
                          type="tel"
                          placeholder="+1234567890"
                          value={registerForm.phone_number}
                          onChange={(e) => setRegisterForm({ ...registerForm, phone_number: e.target.value })}
                          required={registerMethod === "phone"}
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="register-password"
                          data-testid="register-password-input"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={registerForm.password}
                          onChange={(e) => {
                            const newPassword = e.target.value;
                            setRegisterForm((prev) => ({ ...prev, password: newPassword }));
                            setPasswordStrength(checkPasswordStrength(newPassword));
                            setPasswordsMatch(newPassword === registerForm.confirm_password);
                          }}
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
                        </Button>
                      </div>
                      {/* Password strength indicator */}
                      <div className="h-2 w-full rounded-full bg-gray-200 mt-1">
                        <div
                          className={`h-full rounded-full transition-all duration-300
                            ${passwordStrength === 0 && "bg-transparent"}
                            ${passwordStrength === 1 && "bg-red-500 w-1/4"}
                            ${passwordStrength === 2 && "bg-orange-500 w-2/4"}
                            ${passwordStrength === 3 && "bg-yellow-500 w-3/4"}
                            ${passwordStrength === 4 && "bg-green-500 w-full"}
                          `}
                        ></div>
                      </div>
                      {passwordStrength > 0 && (
                        <p className={`text-sm mt-1 ${passwordStrength < 3 ? "text-red-500" : "text-green-500"}`}>
                          {passwordStrength === 1 && "Very Weak"}
                          {passwordStrength === 2 && "Weak"}
                          {passwordStrength === 3 && "Moderate"}
                          {passwordStrength === 4 && "Strong"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm-password">Confirm Password</Label>
                      <div className="relative">
                        <Input
                          id="register-confirm-password"
                          data-testid="register-confirm-password-input"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={registerForm.confirm_password}
                          onChange={(e) => {
                            const newConfirmPassword = e.target.value;
                            setRegisterForm((prev) => ({ ...prev, confirm_password: newConfirmPassword }));
                            setPasswordsMatch(registerForm.password === newConfirmPassword);
                          }}
                          required
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span className="sr-only">{showConfirmPassword ? "Hide password" : "Show password"}</span>
                        </Button>
                      </div>
                      {/* Confirm password match indicator */}
                      {registerForm.confirm_password.length > 0 && (
                        <p className={`text-sm mt-1 ${passwordsMatch ? "text-green-500" : "text-red-500"}`}>
                          {passwordsMatch ? "Passwords match" : "Passwords do not match"}
                        </p>
                      )}
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loading || !passwordsMatch || passwordStrength < 3 || (registerMethod === "email" && !registerForm.email && featureFlags.FEATURE_EMAIL_REGISTRATION) || (registerMethod === "phone" && !registerForm.phone_number && featureFlags.FEATURE_PHONE_REGISTRATION)}
                      data-testid="register-submit-button"
                    >
                      {loading ? "Creating account..." : "Create Account"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <div className="space-y-2 text-center">
                      <p className="text-sm text-slate-600">An OTP has been sent to {registerForm.phone_number}. Please enter it below to verify your account.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="otp">Verification Code</Label>
                      <Input
                        id="otp"
                        data-testid="otp-input"
                        type="text"
                        placeholder="Enter OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        required
                        maxLength={6}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={otpVerificationLoading || otp.length !== 6}>
                      {otpVerificationLoading ? "Verifying..." : "Verify OTP"}
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      className="w-full"
                      onClick={handleSendOtp}
                      disabled={loading || otpResendTimer > 0}
                    >
                      {otpResendTimer > 0 ? `Resend OTP in ${otpResendTimer}s` : "Resend OTP"}
                    </Button>
                  </form>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
